import * as z from "zod";

/**
 * Version of armed screen control: the supervisor advertises it as `armedControlVersion`, and the
 * node agent answers an arm request with it. An answer without it means the node cannot arm.
 */
export const ARMED_CONTROL_VERSION = 1;
/** How long after `issuedAt` a node still accepts a control signal. */
export const SCREEN_CONTROL_SIGNAL_MAX_AGE_MS = 60_000;
/** Longest grant a control signal can carry; longer leases are granted again before they end. */
export const SCREEN_CONTROL_MAX_GRANT_MS = 24 * 60 * 60_000;

/** Opaque identity of the person an armed route was prepared for; never a credential. */
export const ScreenControlHolderSchema = z.string().regex(/^[A-Za-z0-9_:-]{1,200}$/);

/**
 * Asks a node to prepare an input-capable screen whose input stays closed until a lease is
 * granted to `holder` on the route this request registers.
 */
export const ScreenArmRequestSchema = z
  .object({
    interactive: z.literal(false),
    arm: z.literal(true),
    holder: ScreenControlHolderSchema,
  })
  .strict();
export type ScreenArmRequest = z.infer<typeof ScreenArmRequestSchema>;

/**
 * A lease granted to, or revoked from, one armed screen route. The gateway sends it sealed with
 * the node's own token. A grant opens the route's input only for its holder, lease, fence and
 * expiry; a revoke closes it. Neither replaces the control revocation that stops the process.
 */
export const ScreenControlSignalSchema = z
  .object({
    version: z.literal(1),
    routeId: z.string().uuid(),
    action: z.enum(["grant", "revoke"]),
    leaseId: z.string().regex(/^[A-Za-z0-9_-]{1,128}$/),
    fence: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
    holder: ScreenControlHolderSchema,
    expiresAt: z.number().int().positive(),
    issuedAt: z.number().int().positive(),
  })
  .strict();
export type ScreenControlSignal = z.infer<typeof ScreenControlSignalSchema>;
export const SealedScreenControlSchema = z
  .object({
    signal: ScreenControlSignalSchema,
    seal: z.string().regex(/^[a-f0-9]{64}$/),
  })
  .strict();
export type SealedScreenControl = z.infer<typeof SealedScreenControlSchema>;
